const channels = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const lum = (hex) => {
  const [r, g, b] = channels(hex);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};

const scaleHex = (hex, f) => {
  const [r, g, b] = channels(hex);
  const c = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
  return `rgb(${c(r)}, ${c(g)}, ${c(b)})`;
};

const WEAVE =
  "repeating-linear-gradient(45deg, rgba(0,0,0,0.4) 0 2px, transparent 2px 5px), repeating-linear-gradient(-45deg, rgba(0,0,0,0.4) 0 2px, transparent 2px 5px)";

const CarRender = ({ vehicle, color, finish, accents = [], overlayText = "" }) => {
  const ceramic = accents.includes("ceramic_over_wrap");
  const zoneMask = (src) => ({
    WebkitMaskImage: `url(${src})`,
    maskImage: `url(${src})`,
    WebkitMaskSize: "100% 100%",
    maskSize: "100% 100%",
    maskRepeat: "no-repeat",
  });
  const mask = zoneMask(vehicle.mask);

  const sheenBase = { matte: 0.03, satin: 0.16, gloss: 0.46 }[finish] ?? 0.2;
  const darkBoost = lum(color.hex) < 0.15 ? 0.75 : 1;
  const imgFilter = finish === "matte" ? "contrast(0.9) brightness(1.06)" : "none";
  const textColor = lum(color.hex) > 0.62 ? "#101216" : "#F8FAFC";

  return (
    <div
      data-testid="car-render-stage"
      className="relative w-full overflow-hidden"
      style={{
        aspectRatio: "16 / 9",
        containerType: "inline-size",
        background: "radial-gradient(ellipse 80% 75% at 50% 38%, #1B212B 0%, #0A0B0E 78%)",
      }}
    >
      <img
        src={vehicle.img}
        alt={`${vehicle.name} side profile render`}
        className="absolute inset-0 h-full w-full"
        style={{ objectFit: "fill", filter: imgFilter }}
        decoding="async"
        draggable="false"
      />

      {/* darken: maps the silver base down to the true target shade */}
      <div
        className="absolute inset-0"
        style={{ ...mask, background: scaleHex(color.hex, 1.38), mixBlendMode: "multiply" }}
      />
      {/* hue: reinforces the swatch's true color/saturation */}
      <div
        className="absolute inset-0"
        style={{ ...mask, background: color.hex, mixBlendMode: "color", opacity: 0.55 }}
      />

      {accents.includes("chrome_delete") && vehicle.trimMask && (
        <div className="absolute inset-0" style={{ ...zoneMask(vehicle.trimMask), background: "#0B0C0F", opacity: 0.9 }} />
      )}
      {accents.includes("roof_pillars") && vehicle.roofMask && (
        <div
          className="absolute inset-0"
          style={{ ...zoneMask(vehicle.roofMask), background: "linear-gradient(180deg, #23262E 0%, #07080B 60%)" }}
        />
      )}
      {accents.includes("hood_blackout") && vehicle.hoodMask && (
        <>
          <div className="absolute inset-0" style={{ ...zoneMask(vehicle.hoodMask), background: "#101114" }} />
          <div
            className="absolute inset-0"
            style={{ ...zoneMask(vehicle.hoodMask), backgroundImage: WEAVE, opacity: 0.6 }}
          />
        </>
      )}
      {accents.includes("calipers_red") &&
        (vehicle.calipers || []).map((c, i) => (
          <div
            key={i}
            className="absolute pointer-events-none rounded-full"
            style={{
              left: `${c.x}%`,
              top: `${c.y}%`,
              width: `${c.size}cqi`,
              aspectRatio: "1 / 1",
              transform: "translate(-50%, -50%)",
              background:
                "radial-gradient(circle, transparent 38%, rgba(255,42,59,0.95) 48%, rgba(176,14,30,0.9) 58%, transparent 68%)",
            }}
          />
        ))}

      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          ...mask,
          background:
            "linear-gradient(112deg, transparent 32%, rgba(255,255,255,1) 45%, rgba(255,255,255,0.35) 50%, transparent 62%)",
          mixBlendMode: "screen",
          opacity: Math.min(0.8, (sheenBase + (ceramic ? 0.18 : 0)) * darkBoost),
        }}
      />

      {overlayText && (
        <span
          data-testid="car-render-overlay-text"
          className="absolute font-display font-black italic tracking-tight whitespace-nowrap select-none pointer-events-none"
          style={{
            left: `${vehicle.decalZone.x}%`,
            top: `${vehicle.decalZone.y}%`,
            transform: "translate(-50%, -50%) skewX(-8deg)",
            fontSize: "7.5cqi",
            color: textColor,
            textShadow: textColor === "#F8FAFC" ? "0 2px 10px rgba(0,0,0,0.55)" : "0 1px 4px rgba(255,255,255,0.25)",
            opacity: 0.94,
          }}
        >
          {overlayText}
        </span>
      )}
    </div>
  );
};

export default CarRender;
